import React, { useEffect } from 'react'
import { Flex, Text } from '@chilipiper/design-system'
import { Avatar } from '@chilipiper/design-system/src/new'
import { useMessageFeedContext } from '../../chat-widget/MessageFeedContext'
import { LiveMessageBlock } from '../../chat-data/types'
import { getName } from '../../helpers/getName'
import { usePersonalDetails } from '../../api-queries/personal-details'
import { BlockOrCompositeBlock } from '../compositeBlocks'
import { chatPublicEventAction, chiliChatEventName } from '../../constants'
import { ReplyButtonsBlock } from './reply-buttons-block/ReplyButtonsBlock'
import { MessageBlock } from './message-block/MessageBlock'
import { FillFormBlock } from './fill-form-block/FillFormBlock'
import { SuggestedTimes } from './book-meeting-block/SuggestedTimes'
import { BookMeetingBlock } from './book-meeting-block/BookMeetingBlock'
import { ErrorBlock } from './error-block/ErrorBlock'

interface Props {
  block: BlockOrCompositeBlock
  isOwnMessage: boolean
  onLiveMessageVisible: (event: LiveMessageBlock) => void
  isLastBlock: boolean
}

export const BlockRender = ({ block, isOwnMessage, onLiveMessageVisible, isLastBlock }: Props) => {
  const { pickReplyButton } = useMessageFeedContext()

  switch (block.type) {
    case 'Message':
      return <MessageBlock block={block} isOwnMessage={isOwnMessage} />
    case 'PiperAiMessage':
      return <MessageBlock block={block} isOwnMessage={isOwnMessage} />
    case 'LiveMessage':
      return (
        <MessageBlock
          block={block}
          isOwnMessage={isOwnMessage}
          onMessageVisible={onLiveMessageVisible}
        />
      )
    case 'ReplyButtons':
    case 'EnterPiperAiChat':
      return (
        <ReplyButtonsBlock
          block={block}
          onReplyButtonClick={pickReplyButton}
          isLastBlock={isLastBlock}
        />
      )
    case 'FillForm':
      return <FillFormBlock block={block} />
    case 'BookMeeting':
      return <BookMeetingBlock block={block} />
    case 'SuggestedTimes':
      return <SuggestedTimes block={block} />
    case 'SystemMessage':
      if (block.payload.type === 'SystemConversationAssigned') {
        return <AssignmentBlock />
      }
      return <>Unimplemented block</>

    case 'SystemUserResponded':
      return <SystemUserRespondedBlock block={block} />
    case 'SystemChatTransferred':
      return <SystemChatTransferredBlock />
    case 'Error':
      return <ErrorBlock block={block} />
    default:
      return <>Unimplemented block</>
  }
}

const AssignmentBlock = () => {
  return (
    <Flex justifyContent='center' alignItems='center' h={9} w='100%'>
      <Text ml={2} textStyle='detail-m' color='fg/moderate' lineHeight='14px'>
        Waiting for someone to join
      </Text>
    </Flex>
  )
}

const SystemUserRespondedBlock = ({
  block,
}: {
  block: Extract<BlockOrCompositeBlock, { type: 'SystemUserResponded' }>
}) => {
  const { tenantId } = useMessageFeedContext()
  const { data: target } = usePersonalDetails(tenantId, block.author)
  const targetDisplayName = getName(target) ?? '...'
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(chiliChatEventName, {
        detail: { action: chatPublicEventAction.LIVE_CHAT_STARTED },
      })
    )
  }, [])
  return (
    <Flex justifyContent='center' alignItems='center' h={9} w='100%'>
      <Avatar id={block.author} name={targetDisplayName} image={target?.imageUrl} size={5} />
      <Text ml={2} textStyle='detail-m' color='fg/moderate' lineHeight='14px'>
        {targetDisplayName} joined the chat
      </Text>
    </Flex>
  )
}

const SystemChatTransferredBlock = () => {
  return (
    <Flex justifyContent='center' alignItems='center' h={9} w='100%'>
      <Text ml={2} textStyle='detail-m' color='fg/moderate' lineHeight='14px'>
        Chat is being transferred
      </Text>
    </Flex>
  )
}
